import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { storeService } from '../../services/storeService';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { FormField } from '../../components/ui/FormField';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { formatPhoneMask } from '../../utils/phone';
import { formatCpfCnpjMask } from '../../utils/document';
import { BABY_GENDER_SELECT_OPTIONS } from '../../utils/gender';
import { PROMOTION_CAMPAIGN_ENABLED } from '../../config/promotionCampaign';
import { GuestInfo } from './types';

type LookupState = 'idle' | 'searching' | 'found' | 'not-found';

const FIELD_CLASS = 'py-2.5';

const DISABLED_FIELD_CLASS =
  'py-2.5 disabled:border-champagne-dark/40 disabled:text-text-light disabled:bg-champagne/20 disabled:cursor-not-allowed disabled:select-none';

const READONLY_FIELD_CLASS =
  'py-2.5 read-only:border-champagne-dark/50 read-only:text-text-medium read-only:bg-champagne/40 read-only:cursor-not-allowed';

const formatCepMask = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

interface GuestInfoStepProps {
  guestInfo: GuestInfo;
  onChange: (info: GuestInfo) => void;
  onNext: () => void;
}

export function GuestInfoStep({ guestInfo, onChange, onNext }: GuestInfoStepProps) {
  const [errors, setErrors] = useState<Partial<Record<keyof GuestInfo, string>>>({});
  const [showOptional, setShowOptional] = useState(false);
  const [lookupState, setLookupState] = useState<LookupState>('idle');
  const [lookupResult, setLookupResult] = useState<{ maskedName: string; city: string } | null>(null);
  const confirmed = guestInfo.lookupConfirmed;
  const [cep, setCep] = useState(() => formatCepMask(guestInfo.zipCode));
  const [cepLoading, setCepLoading] = useState(false);
  const nextTempBabyId = useRef(-1);

  useEffect(() => {
    const digits = guestInfo.phone.replace(/\D/g, '');

    if (digits.length < 10) {
      setLookupState('idle');
      setLookupResult(null);
      return;
    }

    setLookupState('searching');
    const timer = setTimeout(async () => {
      try {
        const result = await storeService.lookupCustomer(digits);
        setLookupResult(result);
        setLookupState('found');
      } catch {
        setLookupState('not-found');
        setLookupResult(null);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [guestInfo.phone]);

  const handleConfirm = () => {
    onChange({
      ...guestInfo,
      lookupConfirmed: true,
      name: lookupResult?.maskedName ?? '',
      street: '',
      number: '',
      neighborhood: '',
      city: lookupResult?.city ?? '',
      state: '',
      zipCode: '',
      complement: '',
    });
  };

  const clearLookupData = (info: GuestInfo): GuestInfo => ({
    ...info,
    lookupConfirmed: false,
    name: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    complement: '',
  });

  const handleDeny = () => {
    if (confirmed) onChange(clearLookupData(guestInfo));
    setLookupState('not-found');
  };

  const handleChangePhone = () => {
    onChange({ ...clearLookupData(guestInfo), phone: '' });
    setLookupState('idle');
    setLookupResult(null);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextPhone = formatPhoneMask(e.target.value);
    const next = confirmed ? clearLookupData(guestInfo) : guestInfo;
    onChange({ ...next, phone: nextPhone });
  };

  const addBaby = () => {
    onChange({
      ...guestInfo,
      babies: [...guestInfo.babies, { id: nextTempBabyId.current--, name: '', birthdate: '', gender: '' }],
    });
  };

  const removeBaby = (id: number) => {
    onChange({ ...guestInfo, babies: guestInfo.babies.filter((b) => b.id !== id) });
  };

  const updateBabyField = (id: number, field: 'name' | 'birthdate' | 'gender', value: string) => {
    onChange({
      ...guestInfo,
      babies: guestInfo.babies.map((b) => (b.id === id ? { ...b, [field]: value } : b)),
    });
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCep(formatCepMask(e.target.value));
  };

  const handleCepSearch = async () => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const address = await storeService.lookupCep(digits);
      onChange({
        ...guestInfo,
        street: address.street ?? guestInfo.street,
        neighborhood: address.neighborhood ?? guestInfo.neighborhood,
        city: address.city ?? guestInfo.city,
        state: address.state ?? guestInfo.state,
        zipCode: address.zipCode ?? guestInfo.zipCode,
      });
    } catch {
    } finally {
      setCepLoading(false);
    }
  };

  const handleSubmit = () => {
    const newErrors: Partial<Record<keyof GuestInfo, string>> = {};

    if (!guestInfo.phone.trim() || guestInfo.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Celular é obrigatório';
    }

    if (!confirmed) {
      if (!guestInfo.name.trim()) newErrors.name = 'Nome é obrigatório';
      if (!guestInfo.street.trim()) newErrors.street = 'Rua é obrigatória';
      if (!guestInfo.number.trim()) newErrors.number = 'Número é obrigatório';
      if (!guestInfo.neighborhood.trim()) newErrors.neighborhood = 'Bairro é obrigatório';
      if (!guestInfo.city.trim()) newErrors.city = 'Cidade é obrigatória';
      if (!guestInfo.state.trim()) newErrors.state = 'Estado é obrigatório';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onNext();
  };

  const fieldsEnabled = lookupState === 'not-found';

  return (
    <div>
      {!PROMOTION_CAMPAIGN_ENABLED && (
        <div className="flex items-start gap-2.5 sm:gap-3 bg-champagne/60 rounded-xl p-3 sm:p-3.5 mb-4 sm:mb-5 border border-champagne-dark/30">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-4 h-4 text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <p className="text-sm text-text-dark">
              Já tem conta?{' '}
              <Link to="/login" className="text-chocolate font-semibold underline hover:text-chocolate-dark">
                Faça login
              </Link>{' '}
              para acompanhar seus pedidos.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
        <h2 className="text-base font-semibold text-text-dark mb-4">Seus dados</h2>

        <div className="flex flex-col gap-4">
          <FormField label="Celular" required>
            <div className="relative">
              <Input
                type="tel"
                value={guestInfo.phone}
                onChange={handlePhoneChange}
                placeholder="(00) 00000-0000"
                inputMode="numeric"
                readOnly={confirmed}
                error={errors.phone}
                className={`pr-9 ${READONLY_FIELD_CLASS}`}
              />
              {lookupState === 'searching' && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Spinner size="sm" />
                </div>
              )}
              {((lookupState === 'found' && !confirmed) || confirmed) && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg className="w-4 h-4 text-olivine" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
            {lookupState === 'idle' && guestInfo.phone.replace(/\D/g, '').length < 10 && (
              <p className="text-sm text-text-light mt-1">Preencha o celular para liberar os demais campos</p>
            )}
          </FormField>

          {lookupState === 'found' && !confirmed && lookupResult && (
            <div className="bg-champagne/60 border border-champagne-dark/30 rounded-xl p-4">
              <div className="flex items-start gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-dark">Cadastro encontrado!</p>
                  <p className="text-sm text-text-medium mt-0.5">
                    Encontramos um cadastro para <span className="font-medium text-text-dark">{lookupResult.maskedName}</span> em{' '}
                    <span className="font-medium text-text-dark">{lookupResult.city}</span>. Este é você?
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="flex-1 bg-chocolate text-white text-sm font-semibold rounded-lg px-4 py-2.5 hover:bg-chocolate-dark transition-colors"
                >
                  Sim, sou eu
                </button>
                <button
                  type="button"
                  onClick={handleDeny}
                  className="flex-1 border border-champagne-dark text-text-medium text-sm font-medium rounded-lg px-4 py-2.5 hover:border-chocolate hover:text-chocolate hover:bg-champagne/40 transition-colors"
                >
                  Não, continuar
                </button>
              </div>
            </div>
          )}

          {confirmed && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-2.5">
              <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-blue-700">Seus dados cadastrados serão utilizados.</p>
                <p className="text-sm text-blue-600 mt-0.5">
                  Para alterar suas informações, crie uma conta ou solicite a vendedora.
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                  <button
                    type="button"
                    onClick={handleChangePhone}
                    className="text-sm font-medium text-blue-700 underline hover:text-blue-900"
                  >
                    Usar outro celular
                  </button>
                  {!PROMOTION_CAMPAIGN_ENABLED && (
                    <Link to="/login" className="text-sm font-medium text-blue-700 underline hover:text-blue-900">
                      Já tem conta? Faça login
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          {!confirmed && (
            <>
              <FormField label="Nome" required>
                <Input
                  type="text"
                  value={guestInfo.name}
                  onChange={(e) => onChange({ ...guestInfo, name: e.target.value })}
                  placeholder="Nome e Sobrenome"
                  disabled={!fieldsEnabled}
                  error={errors.name}
                  className={DISABLED_FIELD_CLASS}
                />
              </FormField>

              <FormField label="CEP">
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={cep}
                    onChange={handleCepChange}
                    placeholder="00000-000"
                    disabled={!fieldsEnabled}
                    className={`flex-1 ${DISABLED_FIELD_CLASS}`}
                  />
                  <button
                    type="button"
                    onClick={handleCepSearch}
                    disabled={!fieldsEnabled || cepLoading || cep.replace(/\D/g, '').length !== 8}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-chocolate hover:bg-chocolate-dark rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {cepLoading ? '...' : 'Buscar'}
                  </button>
                </div>
                <p className="text-sm text-text-light mt-1">Preenche rua, bairro, cidade e estado automaticamente</p>
              </FormField>

              <FormField label="Rua" required>
                <Input
                  type="text"
                  value={guestInfo.street}
                  onChange={(e) => onChange({ ...guestInfo, street: e.target.value })}
                  placeholder="Sua rua"
                  disabled={!fieldsEnabled}
                  error={errors.street}
                  className={DISABLED_FIELD_CLASS}
                />
              </FormField>

              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <FormField label="Número" required>
                  <Input
                    type="text"
                    value={guestInfo.number}
                    onChange={(e) => onChange({ ...guestInfo, number: e.target.value })}
                    placeholder="Nº"
                    disabled={!fieldsEnabled}
                    error={errors.number}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>

                <FormField label="Complemento">
                  <Input
                    type="text"
                    value={guestInfo.complement}
                    onChange={(e) => onChange({ ...guestInfo, complement: e.target.value })}
                    placeholder="Apto, bloco, etc."
                    disabled={!fieldsEnabled}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <FormField label="Bairro" required>
                  <Input
                    type="text"
                    value={guestInfo.neighborhood}
                    onChange={(e) => onChange({ ...guestInfo, neighborhood: e.target.value })}
                    placeholder="Seu bairro"
                    disabled={!fieldsEnabled}
                    error={errors.neighborhood}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>

                <FormField label="Cidade" required>
                  <Input
                    type="text"
                    value={guestInfo.city}
                    onChange={(e) => onChange({ ...guestInfo, city: e.target.value })}
                    placeholder="Sua cidade"
                    disabled={!fieldsEnabled}
                    error={errors.city}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>

                <FormField label="UF" required>
                  <Input
                    type="text"
                    value={guestInfo.state}
                    onChange={(e) => onChange({ ...guestInfo, state: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="UF"
                    maxLength={2}
                    disabled={!fieldsEnabled}
                    error={errors.state}
                    className={DISABLED_FIELD_CLASS}
                  />
                </FormField>
              </div>
            </>
          )}
        </div>
      </div>

      {!confirmed && (
        <div className={['bg-white rounded-xl shadow-sm overflow-hidden mb-4', !fieldsEnabled ? 'opacity-40 pointer-events-none' : ''].join(' ')}>
          <button
            type="button"
            onClick={() => setShowOptional(!showOptional)}
            disabled={!fieldsEnabled}
            className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-champagne/30 transition-colors disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-2.5">
              <svg className="w-4 h-4 text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-sm font-medium text-text-dark">Dados adicionais</span>
              <span className="text-sm text-text-light">(opcional)</span>
            </div>
            <svg
              className={`w-4 h-4 text-text-light transition-transform duration-200 ${showOptional ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showOptional && (
            <div className="px-5 pb-5 pt-1 border-t border-champagne-dark/20">
              <p className="text-sm text-text-light mb-4">
                Preencha para uma experiência mais personalizada. Se preferir, a vendedora pode completar depois.
              </p>

              <div className="flex flex-col gap-4">
                <FormField label="E-mail">
                  <Input
                    type="email"
                    value={guestInfo.email}
                    onChange={(e) => onChange({ ...guestInfo, email: e.target.value })}
                    placeholder="seu@email.com"
                    className={FIELD_CLASS}
                  />
                </FormField>

                <FormField label="CPF/CNPJ">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={guestInfo.cpfCnpj}
                    onChange={(e) => onChange({ ...guestInfo, cpfCnpj: formatCpfCnpjMask(e.target.value) })}
                    placeholder="000.000.000-00"
                    className={FIELD_CLASS}
                  />
                </FormField>

                <div>
                  <p className="text-sm font-medium text-text-medium mb-2">Filhos(as)</p>

                  {guestInfo.babies.length > 0 && (
                    <div className="flex flex-col gap-3 mb-3">
                      {guestInfo.babies.map((baby) => (
                        <div
                          key={baby.id}
                          className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 sm:gap-3 sm:items-end p-3 rounded-xl bg-champagne/40 border border-champagne-dark/20"
                        >
                          <FormField label="Nome">
                            <Input
                              type="text"
                              value={baby.name}
                              onChange={(e) => updateBabyField(baby.id, 'name', e.target.value)}
                              placeholder="Nome do filho(a)"
                              className={FIELD_CLASS}
                            />
                          </FormField>
                          <FormField label="Nascimento">
                            <Input
                              type="date"
                              value={baby.birthdate}
                              onChange={(e) => updateBabyField(baby.id, 'birthdate', e.target.value)}
                              className={FIELD_CLASS}
                            />
                          </FormField>
                          <FormField label="Gênero">
                            <SearchableSelect
                              options={BABY_GENDER_SELECT_OPTIONS}
                              value={baby.gender ? Number(baby.gender) : undefined}
                              onChange={(id) => updateBabyField(baby.id, 'gender', id != null ? String(id) : '')}
                              placeholder="Não informado"
                              allLabel="Não informado"
                            />
                          </FormField>
                          <button
                            type="button"
                            onClick={() => removeBaby(baby.id)}
                            aria-label="Remover filho(a)"
                            className="w-10 h-10 flex items-center justify-center rounded-lg text-danger hover:bg-danger/10 transition-colors justify-self-start sm:justify-self-auto"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={addBaby}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-chocolate hover:text-chocolate-dark transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Adicionar filho(a)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Button onClick={handleSubmit} className="w-full" disabled={!confirmed && (lookupState === 'searching' || lookupState === 'found')}>
        {lookupState === 'searching' ? 'Verificando...' : 'Continuar'}
      </Button>
    </div>
  );
}
